import React, { useEffect, useState } from "react";

import { select, ZoomTransform } from "d3";

import { useServiceGraphTreeData } from "src/organization/common/containers/ServicesDisplay/hooks/useAppsAndServicesGraph";
import {
  createOrSelectGroup,
  generateAST,
  layout,
  draw,
  positionNodes,
  preProcessEdges,
  scale,
  setHighlightPaths
} from "src/organization/common/containers/ServicesDisplay/util/serviceGraph";

import createEdgePaths from "./edges";

import type { TreeOptions } from "src/organization/common/containers/ServicesDisplay/util/serviceGraph";

export type ServiceTreeEventPayload = {
  x: number;
  y: number;
  size: number;
  metadata: {
    appName?: unknown;
    kind?: unknown;
    name?: string;
    topology?: {
      instances?: string[];
    };
    type?: string;
  };
  class: string;
  icon: string;
};

export interface ServiceTreeProps extends TreeOptions {
  id: string;
  maxHeight: number;
  width?: number | string;
  innerRef: any;
  onLoadEnd?: () => void;
  onNodeClick?: (
    scale: number,
    transform: ZoomTransform
  ) => (e: CustomEvent<ServiceTreeEventPayload>) => void;
  onNodeOut?: (
    scale: number,
    transform: ZoomTransform
  ) => (e: CustomEvent<ServiceTreeEventPayload>) => void;
  onNodeOver?: (
    scale: number,
    transform: ZoomTransform
  ) => (e: CustomEvent<ServiceTreeEventPayload>) => void;
  onKeyDown?: (
    scale: number,
    transform: ZoomTransform
  ) => (e: KeyboardEvent) => void;
}

const ServiceTree = ({
  id,
  maxHeight,
  treePositionY = 10,
  nodeRenderer,
  getNodeDimensions,
  width,
  innerRef,
  onNodeClick,
  onKeyDown,
  onNodeOut,
  onNodeOver,
  onLoadEnd,
  gridStepX = 60,
  gridStepY = 80,
  arcLength = 10,
  midLayerOffset = 40,
  edgeMargin = 10
}: ServiceTreeProps) => {
  const [scaleValue, setScaleValue] = useState(1);
  const [transformValue, setTransformValue] = useState<ZoomTransform>();

  const serviceGraphTreeData = useServiceGraphTreeData();

  useEffect(() => {
    if (!serviceGraphTreeData?.length) return;

    // The user is passing data, and so is doing the order step
    // Generate graph like an AST
    const { graph, sizeX, sizeY } = generateAST(serviceGraphTreeData);

    const options: TreeOptions = {
      sizeX,
      sizeY,
      gridStepX,
      gridStepY,
      arcLength,
      midLayerOffset,
      edgeMargin,
      maxHeight,
      treePositionY,
      nodeRenderer,
      getNodeDimensions
    };

    // Generating layout?
    layout(graph, options);

    // Draw
    const svg = draw(id, graph, options);

    positionNodes(svg, graph, id);

    setHighlightPaths(graph, "router", [], new Set([]));

    preProcessEdges(graph);

    // Get back to the root of the graph to draw the edges
    const d = select(`#${id}`);
    const svgRoot = d.select("svg");
    const outputGroup = createOrSelectGroup(svgRoot, "output");
    const edgePathsGroup = createOrSelectGroup(outputGroup, "edgePaths");
    createEdgePaths(edgePathsGroup, graph, options);

    // Center and scale the graph
    const { scale: s, transform } = scale(id, graph, options);

    setScaleValue(s);
    setTransformValue(transform);
    if (onLoadEnd) onLoadEnd();
  }, [
    serviceGraphTreeData,
    id,
    maxHeight,
    treePositionY,
    nodeRenderer,
    gridStepX,
    gridStepY,
    arcLength,
    midLayerOffset,
    edgeMargin,
    getNodeDimensions,
    onLoadEnd
  ]);

  useEffect(() => {
    const svg = innerRef.current;

    if (!scale || transformValue === undefined) {
      return;
    }

    let onNodeClickHandler: (e: CustomEvent<ServiceTreeEventPayload>) => void;
    let onKeyDownHandler: (e: KeyboardEvent) => void;
    let onNodeOverHandler: (e: CustomEvent<ServiceTreeEventPayload>) => void;
    let onNodeOutHandler: (e: CustomEvent<ServiceTreeEventPayload>) => void;

    // Subscribe to events
    if (onNodeClick) {
      onNodeClickHandler = onNodeClick(scaleValue, transformValue);
      svg.addEventListener("treeSvgClick", onNodeClickHandler, true);
    }
    if (onKeyDown) {
      onKeyDownHandler = onKeyDown(scaleValue, transformValue);
      svg.addEventListener("keydown", onKeyDownHandler, false);
    }
    if (onNodeOver) {
      onNodeOverHandler = onNodeOver(scaleValue, transformValue);
      svg.addEventListener("treeSvgOver", onNodeOverHandler, true);
    }
    if (onNodeOut) {
      onNodeOutHandler = onNodeOut(scaleValue, transformValue);
      svg.addEventListener("treeSvgOut", onNodeOutHandler, true);
    }

    return () => {
      // Unsubscribe from events
      if (onNodeClick) {
        svg.removeEventListener("treeSvgClick", onNodeClickHandler, true);
      }
      if (onKeyDown) {
        svg.removeEventListener("keydown", onKeyDownHandler, false);
      }
      if (onNodeOver) {
        svg.removeEventListener("treeSvgOver", onNodeOverHandler, true);
      }
      if (onNodeOut) {
        svg.removeEventListener("treeSvgOut", onNodeOutHandler, true);
      }
    };
  }, [
    innerRef,
    scaleValue,
    transformValue,
    onNodeClick,
    onKeyDown,
    onNodeOver,
    onNodeOut
  ]);

  return (
    <div
      id={id}
      data-testid="services-tree"
      style={{ width, height: maxHeight }}
      ref={innerRef}
    >
      <svg style={{ overflow: "visible" }}>
        <defs>
          <filter id="shadow" width="200%" height="200%" x="-50%" y="-50%">
            <feDropShadow
              dx="0"
              dy="0.5"
              stdDeviation="2"
              floodColor="rgba(152, 160, 171, 0.4)"
              floodOpacity="1"
            />
          </filter>
          <filter
            id="shadow-hover"
            width="200%"
            height="200%"
            x="-50%"
            y="-50%"
          >
            <feDropShadow
              dx="0"
              dy="3"
              stdDeviation="6"
              floodColor="rgba(75, 97, 128, 0.32)"
              floodOpacity="2"
            />
          </filter>
        </defs>
        <g id="svg-g" />
      </svg>
    </div>
  );
};

export default React.memo(ServiceTree);
